import { useEffect, useState } from 'react';
import { Eye, EyeOff, AlertCircle, Mail } from 'lucide-react';
import SideSection from '../constant/SideSection';
import { useNavigate } from 'react-router-dom';
import { useSignin } from '../hooks/useSignin';
import { useAuthStore } from '../../../hooks/useAuthStore';

const emailRegex = /\S+@\S+\.\S+/;

export default function LoginForm({ isAdmin = false }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const navigate = useNavigate();
  // googleSignin/appleSignin go with the commented-out social buttons below.
  const { signin, adminSignin, loading, error, clearError } = useSignin();
  const { isAdmin: storedIsAdmin } = useAuthStore();

  useEffect(() => {
    if (!isAdmin) return;
    if (storedIsAdmin) {
      navigate("/admin", { replace: true });
    }
  }, [isAdmin, navigate, storedIsAdmin]);

  const validateField = (name, value) => {
    if (name === 'email') {
      const normalizedValue = value.trim().toLowerCase();
      if (!normalizedValue) return 'Email is required';
      if (!emailRegex.test(normalizedValue)) return 'Invalid email address';
    }

    if (name === 'password') {
      if (!value) return 'Password is required';
    }

    return '';
  };

  const runValidation = () => {
    const nextErrors = {};
    const emailError = validateField('email', email);
    const passwordError = validateField('password', password);

    if (emailError) nextErrors.email = emailError;
    if (passwordError) nextErrors.password = passwordError;

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleInputChange = (name, value) => {
    if (error) clearError();

    if (name === 'email') setEmail(value);
    if (name === 'password') setPassword(value);

    setFieldErrors((prev) => {
      const updated = { ...prev };
      const validationMessage = validateField(name, value);
      if (validationMessage) {
        updated[name] = validationMessage;
      } else {
        delete updated[name];
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();

    const isValid = runValidation();
    if (!isValid) return;

    const normalizedEmail = email.trim().toLowerCase();
    const result = isAdmin
      ? await adminSignin(normalizedEmail, password, rememberMe)
      : await signin(normalizedEmail, password, rememberMe);

    if (result.needsVerification && result.redirectTo) {
      navigate(result.redirectTo, { replace: true });
      return;
    }

    if (result.success) {
      const destination = result.redirectTo || (isAdmin ? '/admin' : '/user/travel-assistant');
      navigate(destination, { replace: true });
    }
  };

//   const handleGoogleLogin = () => {
//     clearError();
//     googleSignin();
//   };
//
//   const handleAppleLogin = () => {
//     clearError();
//     appleSignin();
//   };

  const inputBase =
    'w-full rounded-lg border px-4 py-3 text-gray-900 placeholder-gray-400 focus:outline-none transition focus:ring-2 focus:ring-offset-0';
  const errorInput = 'border-red-300 focus:border-red-500 focus:ring-red-200';
  const normalInput = 'border-gray-300 focus:border-[#7a0051] focus:ring-[#7a0051]/30 shadow-sm';

  return (
    <div className="flex min-h-screen bg-white text-gray-900">
      <div className="hidden w-1/2 lg:block">
        <SideSection imageUrl="/images/car4.jpg" />
      </div>

      <div className="flex-1 flex items-center justify-center py-10 px-4">
        <div className="w-full max-w-[36rem] sm:p-8">
          <div className="flex justify-center mb-6">
            <img src="/images/logo.png" className="w-20" alt="PULO logo" />
          </div>
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold capitalize text-gray-900 mb-2">
              {isAdmin ? "Admin login" : (
                <>
                  log <span>in to</span> your account
                </>
              )}
            </h1>
            <p className="text-gray-600 text-sm sm:text-base font-medium">
              {isAdmin
                ? "Manage platform operations with your admin credentials."
                : "Book rides, find local guides, and secure stays all in one place effortlessly."}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="mb-2 block text-sm  text-gray-800">Email</label>
              <div
                className={`relative rounded-lg ${fieldErrors.email ? 'ring-1 ring-red-200' : 'ring-1 ring-transparent'}`}
              >
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-600" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="abdulsimj5@gmail.com"
                  className={`${inputBase} pl-11 text-black font-medium placeholder:font-medium placeholder:text-gray-500   ${fieldErrors.email ? errorInput : normalInput}`}
                  disabled={loading}
                />
              </div>
              {fieldErrors.email && (
                <p className="mt-2 flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  {fieldErrors.email}
                </p>
              )}
            </div>

            <div>
              <label className="mb-2 block text-sm text-gray-800">Password</label>
              <div
                className={`relative rounded-lg ${fieldErrors.password ? 'ring-1 ring-red-200' : 'ring-1 ring-transparent'}`}
              >
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  placeholder="Shamwilu12@"
                  className={`${inputBase} font-medium placeholder:text-gray-500 pr-12 ${fieldErrors.password ? errorInput : normalInput}`}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-500 hover:text-gray-700"
                  disabled={loading}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {fieldErrors.password && (
                <p className="mt-2 flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="h-4 w-4" />
                  {fieldErrors.password}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-[#7a0051] focus:ring-[#7a0051]"
                  disabled={loading}
                />
                <span>Keep me signed in</span>
              </label>
              <button
                type="button"
                onClick={() => navigate('/auth/update-password')}
                className="text-sm font-medium text-[#61013B] hover:underline"
                disabled={loading}
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-[#61013B] py-3 text-base font-semibold text-white shadow-md transition hover:bg-[#640140] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7a0051] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? 'Signing in...' : 'Login'}
            </button>

            {/* Google and Apple sign-in are hidden for now — email and password only.
{!isAdmin ? (
              <>
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  <div className="h-px flex-1 bg-gray-400" />
                  <span className="text-gray-500 font-medium">Or</span>
                  <div className="h-px flex-1 bg-gray-400" />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={loading}
                    className="flex items-center justify-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-gray-300 hover:shadow disabled:opacity-60"
                  >
                    <img src="/icons/google.svg" alt="Google" className="h-5 w-5" />
                    Sign in with Google
                  </button>
                  <button
                    type="button"
                    onClick={handleAppleLogin}
                    disabled={loading}
                    className="flex items-center justify-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-gray-300 hover:shadow disabled:opacity-60"
                  >
                    <img src="/icons/apple.svg" alt="Apple" className="h-5 w-5" />
                    Sign in with Apple
                  </button>
                </div>
              </>
            ) : null}
            */}

            {!isAdmin ? (
              <div className="text-center text-sm font-medium text-gray-700">
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={() => navigate('/auth/welcome')}
                  className="font-semibold text-[#7a0051] underline"
                  disabled={loading}
                >
                  Sign up
                </button>
              </div>
            ) : null}
          </form>
        </div>
      </div>
    </div>
  );
}
